import axios from 'axios';
import logger from '../utils/logger';

// const API_URL = 'https://api.rapidlearningcycles.xantage.co';
const API_URL = import.meta.env.REACT_APP_API_URL || 'https://api.spark.rapidlearningcycles.com';

// Create axios instance with auth header
const getAuthHeader = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
};

/**
 * POST /report-ai/message (FE-5-20).
 *
 * Options are named, not positional: four callers once passed sessionId in
 * the modelId slot, so session_id was always null and the chat had no memory
 * of its own conversation. model_id is not sent (the backend ignores it).
 *
 * @param {string} reportType - 'knowledge_gap' | 'key_decision'
 * @param {string} message - The user's message
 * @param {{ reportId?: string|null, reportContext?: Object|null, sessionId?: string|null }} [options]
 */
const sendReportMessage = async (reportType, message, options = {}) => {
  // A positional call (message, reportId, context, ...) must fail loudly, not drop fields.
  if (options === null || typeof options !== 'object' || Array.isArray(options)) {
    throw new TypeError('reportAiService: pass { reportId, reportContext, sessionId } as the second argument');
  }
  const { reportId = null, reportContext = null, sessionId = null } = options;
  const response = await axios.post(
    `${API_URL}/report-ai/message`,
    {
      message,
      report_id: reportId,
      report_context: reportContext,
      report_type: reportType,
      session_id: sessionId,
    },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Report AI service - handles both KG and KD
const reportAiService = {
  processKGMessage: async (message, options = {}) => {
    try {
      return await sendReportMessage('knowledge_gap', message, options);
    } catch (error) {
      logger.error('Error from KG report AI service:', error);
      throw error;
    }
  },
  

  // CLEAR CONVERSATION MEMORY

  clearReportSession: async (reportId = null, reportType = 'knowledge_gap', sessionId = null) => {
    try {
      const response = await axios.post(
        `${API_URL}/report-ai/clear-session`,
        {
          report_id: reportId,
          report_type: reportType,
          session_id: sessionId
        },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      logger.error('Error clearing report session:', error);
      throw error;
    }
  },

  processKDMessage: async (message, options = {}) => {
    try {
      return await sendReportMessage('key_decision', message, options);
    } catch (error) {
      logger.error('Error from KD report AI service:', error);
      throw error;
    }
  },
  
  // Evaluate a KG report
  evaluateKGReport: async (reportData, modelId = null) => {
    try {
      const response = await axios.post(
        `${API_URL}/report-ai/evaluate`,
        {
          report_data: reportData,
          report_type: 'knowledge_gap',
          model_id: modelId
        },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      logger.error('Error evaluating KG report:', error);
      throw error;
    }
  },
  
  // Evaluate a KD report
  evaluateKDReport: async (reportData, modelId = null) => {
    try {
      const response = await axios.post(
        `${API_URL}/report-ai/evaluate`,
        {
          report_data: reportData,
          report_type: 'key_decision',
          model_id: modelId
        },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      logger.error('Error evaluating KD report:', error);
      throw error;
    }
  },

  checkArchive: async (reportData, reportType, modelId = null) => {
    try {
      // Send full report data to backend - no truncation needed
      // Backend will handle chunking intelligently by sections
      const response = await axios.post(
        `${API_URL}/report-ai/check-archive`,
        {
          report_data: reportData,  // Send full report
          report_type: reportType,  // 'knowledge_gap' or 'key_decision'
          max_results: 10,
          model_id: modelId
        },
        { headers: getAuthHeader() }
      );

      return response.data;
    } catch (error) {
      logger.error('Error checking archive:', error);
      throw error;
    }
  }
};


export default reportAiService;